<template>
  <base-modal-ex :active.sync="isActive"
    :animation-in="animationIn"
    :animation-out="animationOut"
    :animation-duration="animationDuration"
    :can-cancel="false"
    :min-width="60"
    has-modal-card>
    <div class="model-content">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <slot name="text-title" />
          </p>
        </header>
        <section class="modal-card-body">
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label is-required-label">Quote Template Name</label>
                <input v-model="templateName"
                  :class="[
                    'input is-uppercase',
                    {
                      'is-danger': $v.templateName.$dirty && (!$v.templateName.required || !$v.templateName.unique) && templateName
                    }
                  ]"
                  type="text"
                  placeholder="Template Name"
                  maxlength="30">
                <div class="help is-secondary">
                  {{ templateName?.length ?? 0 }}/30
                </div>
                <span v-if="$v.templateName.$dirty && !$v.templateName.required"
                  class="help is-danger ml-1">Name Required</span>
                <span v-if="$v.templateName.$dirty && !$v.templateName.unique && templateName"
                  class="help is-danger ml-1">Name has been taken.</span>
              </div>
            </div>
            <div class="column is-two-fifths">
              <div class="field">
                <label class="label">Add Tag</label>
                <div class="control"
                  style="position:relative;">
                  <div class="dropdown"
                    :class="{ 'is-active': tagDropdownActive }"
                    style="width:100%;">
                    <div class="dropdown-trigger"
                      style="width:100%;">
                      <div class="input tag-input-fake"
                        @click="toggleDropdown">
                        <template v-if="selectedTags.length">
                          <span v-for="tag in selectedTags"
                            :key="tag.tagID"
                            class="tag-short mr-1"
                            :style="{ backgroundColor: tag.colorHex, color: getTextColor(tag.colorHex), marginBottom: '2px' }">
                            {{ tag.shortTag }}
                          </span>
                        </template>
                        <span v-if="!selectedTags.length"
                          style="color:#b5b5b5;">Add tags</span>
                        <span class="icon is-right"
                          style="pointer-events:none;position:absolute;right:10px;top:10px;">
                          <i class="mdi mdi-menu-down" />
                        </span>
                      </div>
                    </div>
                    <TagsDropdownMenu :tags="tags"
                      :selected-tags="selectedTagIDs"
                      :is-visible="tagDropdownActive"
                      @toggle-tag="toggleTag"
                      @clear-all-tags="clearAllTags" />

                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="field">
            <label class="label">Template Type</label>
            <div class="select is-fullwidth">
              <select v-model="quoteType">
                <option value="null"
                  disabled>Select</option>
                <option v-for="(type, index) in quoteTemplateTypes"
                  :key="index">{{ type }}</option>
              </select>
            </div>
            <span v-if="$v.quoteType.$dirty && !$v.quoteType.required"
              class="help is-danger ml-1">Template Type Required</span>
          </div>

          <div class="field">
            <label class="label">Vehicle Body Style <span class="has-text-grey is-italic">(Required for Labour)</span> </label>
            <div class="select is-fullwidth">
              <select v-model="bodyId">
                <option value="null"
                  disabled>Select</option>
                <option v-for="(style, index) in bodyStyles"
                  :key="index"
                  :value="style.id">{{ style.name }}</option>
              </select>
            </div>
          </div>

          <div class="field">
            <label class="label">Labour Times <span class="has-text-grey is-italic">(Required for Labour)</span> </label>
            <div class="select is-fullwidth">
              <select v-model="timesType">
                <option value="null"
                  disabled>Select</option>
                <option v-for="(type, index) in labourTimesTypes"
                  :key="index">{{ type }}</option>
              </select>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot"
          :style="`justify-content: flex-${buttonLocation}`">
          <button class="button is-primary"
            @click="add">Create</button>
          <button class="button"
            @click="close">Close</button>
        </footer>
      </div>
    </div>
  </base-modal-ex>
</template>
<script>
import BaseModalEx from '@/components/BulmaModal/BaseModalEx'
import { LabourTimeTypes, QuoteTemplateTypes } from '@/enums'
import { QuoteTemplateModel } from '@/classes/viewmodels'
import Guid from '@/components/Guid'
import { required } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import QuoteTemplateRoutes from '@/views/quotetemplate/route-types'
import QuoteTemplateService from '@/services/QuoteTemplateService'
import TagsDropdownMenu from '../../views/manageTag/TagsDropdownMenu.vue'
import { getTextColor } from '@/utils/ColorFunctions'

export default {
  name: 'QuoteNewTemplateModal',
  components: {
    BaseModalEx,
    TagsDropdownMenu
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    animationIn: {
      type: String,
      default: 'fadeIn'
    },
    animationOut: {
      type: String,
      default: 'fadeOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    },
    buttonLocation: {
      type: String,
      default: 'end'
    },
    templates: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      bodyId: null,
      isActive: this.active || false,
      templateName: null,
      timesType: null,
      quoteType: null,
      selectedTagIDs: [],
      tagDropdownActive: false
    }
  },
  validations: {
    templateName: {
      required,
      unique(value) {
        return !this.templateNames.includes(value.toUpperCase())
      }
    },
    quoteType: { required }
  },
  computed: {
    ...mapGetters('vehicleOptions', {
      storeBodyShapes: 'bodyShapes'
    }),
    bodyStyles() {
      return this.storeBodyShapes
    },
    labourTimesTypes() {
      return LabourTimeTypes
    },
    quoteTemplateTypes() {
      return Object.fromEntries(Object.entries(QuoteTemplateTypes).filter(([, value]) => value !== 'Insurer'))
    },
    templateNames() {
      return this.templates.map((template) => template.name.toUpperCase())
    },
    selectedTags() {
      return this.tags.filter((tag) => this.selectedTagIDs.includes(tag.tagID))
    }
  },
  watch: {
    active(value) {
      this.isActive = value
    },
    templateName: {
      handler: function (val) {
        if (val) {
          // Debounce validation to avoid too many checks while typing
          clearTimeout(this.validationTimeout)
          this.validationTimeout = setTimeout(() => {
            this.$v.templateName.$touch()
          }, 300)
        } else {
          // Clear validation state when field is empty
          this.$v.templateName.$reset()
        }
      },
      immediate: false
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    async add() {
      this.$v.$touch()
      if (this.$v.$error) return

      let templateData = {
        templateId: Guid.empty(),
        bodyId: this.bodyId,
        companyId: this.$company.info.companyId,
        name: this.templateName.toUpperCase(),
        quoteType: Object.keys(QuoteTemplateTypes).find((key) => QuoteTemplateTypes[key] == this.quoteType),
        timesType: this.timesType,
        tags: this.selectedTags
      }
      const newTemplate = new QuoteTemplateModel(templateData)
      const template = await QuoteTemplateService.insertQuoteTemplate(newTemplate)
      this.$router.push({
        path: `${QuoteTemplateRoutes.QuoteTemplateListView.path}/${template.templateId}`
      })
    },
    close() {
      this.isActive = false
      this.$emit('update:active', false)
    },
    toggleTag(tag) {
      const idx = this.selectedTagIDs.indexOf(tag.tagID)
      if (idx === -1) {
        this.selectedTagIDs.push(tag.tagID)
      } else {
        this.selectedTagIDs.splice(idx, 1)
      }
    },
    getTextColor,
    toggleDropdown() {
      this.tagDropdownActive = !this.tagDropdownActive
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.dropdown')
      if (dropdown && !dropdown.contains(event.target)) {
        this.tagDropdownActive = false
      }
    },
    clearAllTags() {
      this.selectedTagIDs = []
    }
  }
}
</script>

<style scoped>
.modal-card {
  width: 60vw;
  min-width: 800px;
}
</style>

